import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);
const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'nq_token';

function formatApiError(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map(e => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

// Axios instance with auto Authorization header
const authAxios = axios.create();
authAxios.interceptors.request.use(config => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Fetch wrapper with auto Authorization header (used in components)
function authFetch(url, options = {}) {
  const token = localStorage.getItem(TOKEN_KEY);
  return fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = not auth
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  async function checkAuth() {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await authAxios.get(`${API}/api/auth/me`);
      setUser(data);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setUser(false);
    } finally {
      setLoading(false);
    }
  }

  async function login(email, password) {
    const { data } = await axios.post(`${API}/api/auth/login`, { email, password });
    localStorage.setItem(TOKEN_KEY, data.access_token);
    const { access_token, ...userInfo } = data;
    setUser(userInfo);
    return userInfo;
  }

  async function register(name, email, password, role) {
    const { data } = await axios.post(`${API}/api/auth/register`, { name, email, password, role });
    localStorage.setItem(TOKEN_KEY, data.access_token);
    const { access_token, ...userInfo } = data;
    setUser(userInfo);
    return userInfo;
  }

  async function logout() {
    localStorage.removeItem(TOKEN_KEY);
    setUser(false);
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, formatApiError, setUser, authFetch }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
